import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    User as FirebaseUser,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut as firebaseSignOut,
    sendPasswordResetEmail,
    onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { User, AuthContextType } from '../types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
            if (firebaseUser) {
                // First, try to get user data from users collection
                const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    setUser({
                        uid: firebaseUser.uid,
                        email: firebaseUser.email || '',
                        displayName: userData.displayName,
                        role: userData.role,
                        createdAt: userData.createdAt?.toDate() || new Date(),
                        updatedAt: userData.updatedAt?.toDate() || new Date(),
                    });
                } else {
                    // If not found in users collection, check other collections
                    let userData = null;
                    let userRole = null;
                    let displayName = '';

                    // Check staff collection (document ID = UID)
                    const staffDoc = await getDoc(doc(db, 'staff', firebaseUser.uid));

                    if (staffDoc.exists()) {
                        const staffData = staffDoc.data();
                        userData = staffData;
                        userRole = staffData.role;
                        displayName = `${staffData.firstName} ${staffData.lastName}`;
                    } else {
                        // Check teachers collection (document ID = UID)
                        const teacherDoc = await getDoc(doc(db, 'teachers', firebaseUser.uid));

                        if (teacherDoc.exists()) {
                            const teacherData = teacherDoc.data();
                            userData = teacherData;
                            userRole = 'teacher';
                            displayName = `${teacherData.firstName} ${teacherData.lastName}`;
                        } else {
                            // Check students collection (document ID = UID)
                            const studentDoc = await getDoc(doc(db, 'students', firebaseUser.uid));

                            if (studentDoc.exists()) {
                                const studentData = studentDoc.data();
                                userData = studentData;
                                userRole = 'student';
                                displayName = `${studentData.firstName} ${studentData.lastName}`;
                            }
                        }
                    }

                    // If user data found in any collection, set the user
                    if (userData && userRole) {
                        setUser({
                            uid: firebaseUser.uid,
                            email: firebaseUser.email || '',
                            displayName: displayName,
                            role: userRole,
                            createdAt: userData.createdAt?.toDate() || new Date(),
                            updatedAt: userData.updatedAt?.toDate() || new Date(),
                        });
                    }
                }

                // Always redirect to dashboard after successful login
                const attemptedLocation = sessionStorage.getItem('attemptedLocation');
                if (attemptedLocation && attemptedLocation !== '/dashboard') {
                    // Only redirect if it's not already dashboard
                    window.location.href = '/dashboard';
                }
            } else {
                setUser(null);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const signIn = async (email: string, password: string) => {
        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (error) {
            throw error;
        }
    };

    const signUp = async (email: string, password: string, displayName: string) => {
        try {
            const { user: firebaseUser } = await createUserWithEmailAndPassword(auth, email, password);

            // Create user document in Firestore with admin role
            const userData = {
                displayName,
                role: 'admin' as const,
                authorities: { all: true },
                createdAt: new Date(),
                updatedAt: new Date(),
            };

            await setDoc(doc(db, 'users', firebaseUser.uid), userData);
        } catch (error) {
            throw error;
        }
    };

    const signOut = async () => {
        try {
            await firebaseSignOut(auth);
        } catch (error) {
            throw error;
        }
    };

    const resetPassword = async (email: string) => {
        try {
            await sendPasswordResetEmail(auth, email);
        } catch (error) {
            throw error;
        }
    };

    const value: AuthContextType = {
        user,
        loading,
        signIn,
        signUp,
        signOut,
        resetPassword,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
